import React, { useEffect, useState } from "react";
import { api, formatMXN } from "../lib/api";
import { X, UtensilsCrossed, Loader2, FileText } from "lucide-react";

export default function MenuModal({ businessId, businessName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/directory/menu/${businessId}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ categories: [] }))
      .finally(() => setLoading(false));
  }, [businessId]);

  const categories = data?.categories?.filter((c) => c.items?.length > 0) || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="menu-modal-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <div className="overline text-xs text-muted-foreground">Menú / Catálogo</div>
            <h2 className="font-display text-xl tracking-tight">{businessName}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted"
            data-testid="menu-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {loading && (
            <div className="py-12 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Cargando menú…
            </div>
          )}

          {!loading && data?.menu_pdf_base64 && (
            <a
              href={data.menu_pdf_base64}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-3.5 hover:bg-[#EDE9FE] transition-colors"
              data-testid="menu-modal-pdf-btn"
            >
              <FileText className="w-5 h-5 text-[#7C3AED] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#5B21B6]">Ver menú en PDF</div>
                <div className="text-xs text-muted-foreground">Abre o descarga el menú completo</div>
              </div>
            </a>
          )}

          {!loading && categories.length === 0 && !data?.menu_pdf_base64 && (
            <div className="py-12 text-center text-muted-foreground">
              <UtensilsCrossed className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Este negocio aún no ha publicado su menú.</p>
            </div>
          )}

          {!loading && categories.map((cat) => (
            <div key={cat.id}>
              <h3 className="font-bold text-[11px] uppercase tracking-[0.2em] text-[#7C3AED] mb-3">
                {cat.name}
              </h3>
              <div className="space-y-0">
                {cat.items.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-start gap-3 py-3 border-b border-border last:border-0 ${
                      !item.available ? "opacity-50" : ""
                    }`}
                    data-testid={`menu-public-item-${item.id}`}
                  >
                    {item.image_base64 && (
                      <img src={item.image_base64} alt={item.name} className="w-12 h-12 rounded-lg object-cover shrink-0 border border-border" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{item.name}</span>
                        {!item.available && (
                          <span className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium">
                            No disponible
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.description}</p>
                      )}
                    </div>
                    {item.price != null && (
                      <span className="font-semibold text-sm text-[#5B21B6] shrink-0">
                        {formatMXN(item.price)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
